import React, { Suspense, lazy, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../AuthContext";
import "../styles/pages/LandingPage.css";
import SearchBar from "../components/common/SearchBar";
import AuthModal from "../components/user/AuthModal";
import useHeroBackground from "../components/landing_page/useHeroBackground";

const LazySpinningGlobe = lazy(() => import("../components/landing_page/SpinningGlobe"));

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bg: heroBg, alt: heroAlt } = useHeroBackground();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("failed")) {
      import("react-toastify").then(({ toast }) =>
        toast.error("Payment canceled or failed. Please try again.", {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      );
      navigate("/", { replace: true });
    }
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>WeiFinders – Discover Your Next Adventure</title>
        <meta
          name="description"
          content="Explore travel guides, discover destinations and build itineraries on WeiFinders."
        />
        <meta property="og:title" content="WeiFinders – Discover Your Next Adventure" />
        <meta
          property="og:description"
          content="Explore travel guides, discover destinations and build itineraries on WeiFinders."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://weifinders.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content="WeiFinders – Discover Your Next Adventure" />
        <meta
          name="twitter:description"
          content="Explore travel guides, discover destinations and build itineraries on WeiFinders."
        />
      </Helmet>

      <main className="landing-page" role="main" style={{ backgroundImage: heroBg }}>
        <span className="visually-hidden">{heroAlt}</span>

        <header className="landing-page-title">
          <img src="/weifinders_logo.svg" alt="WeiFinders logo" className="landing-page-logo" />
        </header>

        <div className="globe-container">
          <Suspense fallback={null}>
            <LazySpinningGlobe />
          </Suspense>
        </div>

        <div className="landing-page-controls">
          <nav className="landing-page-buttons" aria-label="Primary Navigation">
            {user ? (
              <Link
                to="/explore/ai-agent"
                className="landing-page-login-button landing-page-nav-button"
              >
                Plan with AI
              </Link>
            ) : (
              <AuthModal
                triggerElement={
                  <button className="landing-page-login-button landing-page-nav-button">
                    Plan with AI
                  </button>
                }
              />
            )}

            <Link
              to="/destination/browse"
              className="landing-page-login-button landing-page-nav-button"
            >
              Explore Guides
            </Link>
          </nav>

          <section className="landing-page-search">
            <SearchBar navigate={navigate} variant="landing" />
          </section>

          <section className="landing-page-login">
            {user ? (
              <Link to="/account" className="landing-page-login-button">
                My Account
              </Link>
            ) : (
              <AuthModal
                triggerElement={<button className="landing-page-login-button">Login / Sign up</button>}
              />
            )}
          </section>
        </div>
      </main>
    </>
  );
};

export default LandingPage;
